import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash, X, PaperPlaneTilt, MagnifyingGlass, Buildings, ClockCounterClockwise, Briefcase } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const TYPES = ["retailer", "sponsor", "collab", "donor", "customer", "press", "other"];
const STATUSES = ["new", "contacted", "replied", "won", "lost"];
const STATUS_COLOR = {
  new: "#8a8a8a", contacted: "#00e5e5", replied: "#f2ff00", won: "#ccff00", lost: "#ff4d4d",
};

const TEMPLATES = [
  { key: "sponsor", label: "Sponsorship / Donor", subject: "Partner with Mr. Wigglez — save 3,000 pits",
    body: "Hi {{first_name}},\n\nI'm reaching out from Mr. Wigglez — a pitbull streetwear brand on a mission to build a $3M, 300-acre sanctuary and rescue 3,000 pits.\n\n{{company}} shares our values, and I'd love to explore a sponsorship or donor partnership that puts your brand in front of a loyal, mission-driven audience while funding the ranch.\n\nOpen to a quick 15-minute call this week?\n\n— The Mr. Wigglez Pack" },
  { key: "wholesale", label: "Wholesale / Stock our line", subject: "Stock Mr. Wigglez in your stores",
    body: "Hi {{first_name}},\n\nMr. Wigglez is a high-end pitbull streetwear line (premium-grade quality, hypercolor neon, human + dog matching sets) with a rescue mission built in.\n\nI think it would move fast with {{company}}'s customers. I'd love to send a wholesale linesheet and talk terms.\n\nWorth a conversation?\n\n— The Mr. Wigglez Pack" },
  { key: "collab", label: "Collab / Brand partnership", subject: "Collab idea: {{company}} x Mr. Wigglez",
    body: "Hi {{first_name}},\n\nBig fan of what {{company}} is building. Mr. Wigglez fuses classy-hood streetwear with a real rescue mission, and I see a natural collab here.\n\nCan we jam on a limited drop together?\n\n— The Mr. Wigglez Pack" },
  { key: "customer", label: "VIP customer welcome", subject: "Welcome to the pack, {{first_name}} 🐾",
    body: "Hi {{first_name}},\n\nWelcome to Mr. Wigglez. Every piece you wear puts a pit off the street and a kid closer to home.\n\nHere's early access to our next drop — thank you for repping the movement.\n\n— The Mr. Wigglez Pack" },
];

const EMPTY = {
  first_name: "", last_name: "", company: "", title: "", email: "", phone: "", website: "",
  linkedin: "", city: "", state: "", country: "", lead_type: "retailer", status: "new",
  source: "", est_value: 0, tags: "", notes: "",
};

export default function EnterpriseLeads() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [selected, setSelected] = useState([]);
  const [showOutreach, setShowOutreach] = useState(false);
  const [out, setOut] = useState({ subject: TEMPLATES[0].subject, body: TEMPLATES[0].body });
  const [busy, setBusy] = useState(false);
  const [logFor, setLogFor] = useState(null);

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (typeFilter) params.lead_type = typeFilter;
    if (statusFilter) params.status = statusFilter;
    api.get("/admin/leads", { params }).then(({ data }) => setLeads(data)).catch(() => {});
    api.get("/admin/leads/stats").then(({ data }) => setStats(data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, typeFilter, statusFilter]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (l) => {
    setEditing(l.id);
    setForm({ ...EMPTY, ...l, est_value: l.est_value || 0, tags: (l.tags || []).join(", ") });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.email.trim()) return toast.error("Email is required");
    setBusy(true);
    const payload = { ...form, est_value: Number(form.est_value) || 0, tags: form.tags ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : [] };
    try {
      if (editing) await api.put(`/admin/leads/${editing}`, payload);
      else await api.post("/admin/leads", payload);
      toast.success(editing ? "Lead updated" : "Lead added");
      setShowForm(false); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setStatus = async (l, status) => {
    await api.put(`/admin/leads/${l.id}`, { status }).catch(() => {});
    load();
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete ${l.first_name || l.email}?`)) return;
    await api.delete(`/admin/leads/${l.id}`).catch(() => {});
    setSelected((s) => s.filter((id) => id !== l.id));
    load();
  };

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allVisible = leads.map((l) => l.id);
  const toggleAll = () => setSelected((s) => s.length === allVisible.length ? [] : allVisible);

  const applyTemplate = (t) => setOut({ subject: t.subject, body: t.body });

  const sendOutreach = async () => {
    if (selected.length === 0) return toast.error("Select at least one lead");
    if (!out.subject.trim() || !out.body.trim()) return toast.error("Subject and message required");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/leads/outreach", { lead_ids: selected, subject: out.subject, message_html: out.body });
      toast.success(`Outreach sent to ${data.sent}/${data.total} leads 🚀`);
      setShowOutreach(false); setSelected([]); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const statCells = useMemo(() => [
    { l: "Total leads", n: stats.total, c: "#fff" },
    ...STATUSES.map((s) => ({ l: s, n: stats.by_status?.[s] || 0, c: STATUS_COLOR[s] })),
  ], [stats]);

  return (
    <div data-testid="admin-enterprise">
      <div className="flex items-center gap-3 mb-2">
        <Briefcase weight="fill" size={20} className="text-primary" />
        <h2 className="font-display text-2xl">Mr. Wigglez Enterprises · Leads CRM</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-6 max-w-2xl">Your customer & partner database. Add CEOs and retailers, track the pipeline, and send branded outreach in one tap — replies land in {`Rjwmelinhatman@gmail.com`}.</p>

      {/* Stats */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-6" data-testid="leads-stats">
        {statCells.map((s) => (
          <div key={s.l} className="rounded-xl border border-white/10 bg-card py-4 text-center">
            <p className="text-2xl font-modern font-semibold" style={{ color: s.c }}>{s.n}</p>
            <p className="text-[11px] text-muted-foreground mt-1 uppercase tracking-wide">{s.l}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, company, email…"
            className="w-full bg-transparent border border-white/15 rounded-full pl-9 pr-4 py-2.5 text-sm outline-none focus:border-primary" data-testid="leads-search" />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="bg-card border border-white/15 rounded-full px-4 py-2.5 text-sm outline-none" data-testid="leads-type-filter">
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-card border border-white/15 rounded-full px-4 py-2.5 text-sm outline-none" data-testid="leads-status-filter">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="md:ml-auto flex gap-2">
          <button onClick={() => selected.length ? setShowOutreach(true) : toast.error("Select leads first")}
            className="inline-flex items-center gap-2 bg-secondary text-black px-5 py-2.5 rounded-full font-modern text-sm disabled:opacity-40" data-testid="leads-outreach-open" disabled={selected.length === 0}>
            <PaperPlaneTilt weight="fill" size={15} /> Email {selected.length || ""}
          </button>
          <button onClick={openNew} className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full font-modern text-sm" data-testid="leads-add-open">
            <Plus weight="bold" size={15} /> Add lead
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-white/10 overflow-hidden" data-testid="leads-table">
        <div className="grid grid-cols-[32px_1.4fr_1fr_0.8fr_1fr_60px_80px] gap-2 px-4 py-3 bg-white/5 text-[11px] uppercase tracking-wide text-muted-foreground items-center">
          <input type="checkbox" checked={selected.length > 0 && selected.length === allVisible.length} onChange={toggleAll} data-testid="leads-select-all" />
          <span>Name / Company</span><span>Type</span><span>Status</span><span>Email</span><span>Log</span><span></span>
        </div>
        {leads.length === 0 ? (
          <p className="text-muted-foreground text-center py-12" data-testid="leads-empty">No leads yet — add your first CEO.</p>
        ) : leads.map((l) => (
          <div key={l.id} className="grid grid-cols-[32px_1.4fr_1fr_0.8fr_1fr_60px_80px] gap-2 px-4 py-3 border-t border-white/5 items-center text-sm" data-testid={`lead-row-${l.id}`}>
            <input type="checkbox" checked={selected.includes(l.id)} onChange={() => toggle(l.id)} data-testid={`lead-check-${l.id}`} />
            <div className="min-w-0">
              <button onClick={() => openEdit(l)} className="font-modern text-white hover:text-primary transition-colors truncate block text-left" data-testid={`lead-name-${l.id}`}>
                {(l.first_name || l.last_name) ? `${l.first_name} ${l.last_name}`.trim() : "(no name)"}
              </button>
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1 truncate"><Buildings size={11} /> {l.company || "—"}{l.title ? ` · ${l.title}` : ""}</span>
            </div>
            <span className="text-xs text-muted-foreground capitalize">{l.lead_type}</span>
            <select value={l.status} onChange={(e) => setStatus(l, e.target.value)} className="bg-transparent border border-white/15 rounded-md px-2 py-1 text-xs outline-none" style={{ color: STATUS_COLOR[l.status] }} data-testid={`lead-status-${l.id}`}>
              {STATUSES.map((s) => <option key={s} value={s} style={{ color: "#000" }}>{s}</option>)}
            </select>
            <a href={`mailto:${l.email}`} className="text-xs text-secondary truncate hover:underline">{l.email}</a>
            <button onClick={() => setLogFor(l)} className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-white" data-testid={`lead-log-${l.id}`}>
              <ClockCounterClockwise size={13} /> {l.contacted_count || 0}
            </button>
            <div className="flex gap-1 justify-end">
              <button onClick={() => remove(l)} className="h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:text-red-400 hover:bg-red-500/10" data-testid={`lead-delete-${l.id}`}><Trash size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] bg-black/85 backdrop-blur grid place-items-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} className="w-full max-w-2xl rounded-2xl border border-primary/30 bg-[#0b0b0b] p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="lead-form-modal">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display text-2xl">{editing ? "Edit lead" : "Add lead"}</h3>
                <button onClick={() => setShowForm(false)} className="text-muted-foreground hover:text-white"><X size={22} /></button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {[["first_name", "First name"], ["last_name", "Last name"], ["company", "Company"], ["title", "Title / Role"], ["email", "Email *"], ["phone", "Phone"], ["website", "Website"], ["linkedin", "LinkedIn"], ["city", "City"], ["state", "State"], ["country", "Country"], ["source", "Source (e.g. eagle-leads)"]].map(([k, label]) => (
                  <input key={k} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} placeholder={label}
                    className="bg-transparent border border-white/15 rounded-[10px] px-3 py-2.5 text-sm outline-none focus:border-primary" data-testid={`lead-field-${k}`} />
                ))}
                <select value={form.lead_type} onChange={(e) => setForm((f) => ({ ...f, lead_type: e.target.value }))} className="bg-card border border-white/15 rounded-[10px] px-3 py-2.5 text-sm outline-none" data-testid="lead-field-lead_type">
                  {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className="bg-card border border-white/15 rounded-[10px] px-3 py-2.5 text-sm outline-none" data-testid="lead-field-status">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input type="number" value={form.est_value} onChange={(e) => setForm((f) => ({ ...f, est_value: e.target.value }))} placeholder="Est. value ($)" className="bg-transparent border border-white/15 rounded-[10px] px-3 py-2.5 text-sm outline-none focus:border-primary" data-testid="lead-field-est_value" />
                <input value={form.tags} onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))} placeholder="Tags (comma separated)" className="bg-transparent border border-white/15 rounded-[10px] px-3 py-2.5 text-sm outline-none focus:border-primary" data-testid="lead-field-tags" />
              </div>
              <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Notes…" rows={3} className="w-full mt-3 bg-transparent border border-white/15 rounded-[10px] px-3 py-2.5 text-sm outline-none focus:border-primary resize-none" data-testid="lead-field-notes" />
              <button onClick={save} disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 rounded-[10px] font-modern font-medium disabled:opacity-50" data-testid="lead-save">
                <Plus weight="bold" size={16} /> {busy ? "Saving…" : editing ? "Save changes" : "Add lead"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Outreach modal */}
      <AnimatePresence>
        {showOutreach && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] bg-black/85 backdrop-blur grid place-items-center p-4 overflow-y-auto" onClick={() => setShowOutreach(false)}>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} className="w-full max-w-2xl rounded-2xl border border-secondary/40 bg-[#0b0b0b] p-6 my-8" onClick={(e) => e.stopPropagation()} data-testid="outreach-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-2xl">Email {selected.length} lead{selected.length > 1 ? "s" : ""}</h3>
                <button onClick={() => setShowOutreach(false)} className="text-muted-foreground hover:text-white"><X size={22} /></button>
              </div>
              <p className="text-xs text-muted-foreground mb-3">Placeholders: <code className="text-secondary">{`{{first_name}} {{company}} {{title}} {{city}}`}</code> — personalized per lead. Sent as a branded Mr. Wigglez Enterprises email; replies go to your inbox.</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {TEMPLATES.map((t) => (
                  <button key={t.key} onClick={() => applyTemplate(t)} className="overline px-3 py-1.5 rounded-full border border-white/15 text-muted-foreground hover:text-white hover:border-secondary transition-colors" data-testid={`outreach-tpl-${t.key}`}>{t.label}</button>
                ))}
              </div>
              <input value={out.subject} onChange={(e) => setOut((o) => ({ ...o, subject: e.target.value }))} placeholder="Subject" className="w-full bg-transparent border border-white/15 rounded-[10px] px-4 py-3 text-sm outline-none focus:border-secondary mb-3" data-testid="outreach-subject" />
              <textarea value={out.body} onChange={(e) => setOut((o) => ({ ...o, body: e.target.value }))} rows={9} className="w-full bg-transparent border border-white/15 rounded-[10px] px-4 py-3 text-sm outline-none focus:border-secondary resize-none" data-testid="outreach-body" />
              <button onClick={sendOutreach} disabled={busy} className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-secondary text-black py-3 rounded-[10px] font-modern font-medium disabled:opacity-50" data-testid="outreach-send">
                <PaperPlaneTilt weight="fill" size={16} /> {busy ? "Sending…" : `Send to ${selected.length}`}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Log drawer */}
      <AnimatePresence>
        {logFor && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[90] bg-black/85 backdrop-blur grid place-items-center p-4" onClick={() => setLogFor(null)}>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} className="w-full max-w-md rounded-2xl border border-white/15 bg-[#0b0b0b] p-6" onClick={(e) => e.stopPropagation()} data-testid="lead-log-modal">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display text-xl">Outreach log · {logFor.first_name || logFor.email}</h3>
                <button onClick={() => setLogFor(null)} className="text-muted-foreground hover:text-white"><X size={20} /></button>
              </div>
              {(logFor.outreach_log || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No emails sent yet.</p>
              ) : (
                <div className="space-y-2">
                  {logFor.outreach_log.slice().reverse().map((e, i) => (
                    <div key={i} className="rounded-lg border border-white/10 p-3 text-sm">
                      <p className="text-white">{e.subject}</p>
                      <p className="text-xs text-muted-foreground mt-1">{new Date(e.sent_at).toLocaleString()} · <span style={{ color: e.ok ? "#ccff00" : "#ff4d4d" }}>{e.ok ? "delivered" : "failed"}</span></p>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
